import {
   ChangeDetectionStrategy,
   ChangeDetectorRef,
   Component,
   ViewChild
} from '@angular/core';
import * as shortid from 'shortid';
import { PanelBaseComponent } from '../panels/panel-base.component';
import { MessageBusService } from '../message-bus.service';
import { UserSettingsService } from '../user-settings.service';
import { TradingInstrument } from '../trading-instruments/trading-instrument.class';
import { TradingInstrumentsService } from '../trading-instruments/trading-instruments-service.interface';
import { OverlaySnapshot, PriceChartComponent } from './price-chart.component';

const SCHEMA_VERSION = 2 as const;
const STORAGE_KEY = 'chart-price.settings';

interface PriceChartTickerState {
   resolution: string;
   chartType: string;
   maEnabled: boolean;
   maPeriod: number;
   maColor: string;
   pivotsEnabled: boolean;
   pivotUpColor: string;
   pivotDownColor: string;
   overlays: OverlaySnapshot[];
}

interface PriceChartPanelState {
   schemaVersion: typeof SCHEMA_VERSION;
   lastTicker: string;
   perTicker: { [ticker: string]: PriceChartTickerState };
}

interface LegacyPriceChartPanelState {
   ticker: string;
   resolution: string;
   chartType?: string;
   maEnabled?: boolean;
   maPeriod?: number;
   maColor?: string;
   pivotsEnabled?: boolean;
   pivotUpColor?: string;
   pivotDownColor?: string;
}

const DEFAULT_MA_PERIOD = 10;
const DEFAULT_MA_COLOR = '#26a69a';
const DEFAULT_CHART_TYPE = 'candle_solid';
const DEFAULT_PIVOT_UP_COLOR = '#26a69a';
const DEFAULT_PIVOT_DOWN_COLOR = '#ef5350';

const CHART_TYPES: { value: string; label: string }[] = [
   { value: 'candle_solid', label: 'Candles' },
   { value: 'ohlc',         label: 'Bars' },
   { value: 'area',         label: 'Line' }
];

const RESOLUTIONS: { value: string; label: string }[] = [
   { value: '1',   label: '1m' },
   { value: '5',   label: '5m' },
   { value: '15',  label: '15m' },
   { value: '30',  label: '30m' },
   { value: '60',  label: '1H' },
   { value: '240', label: '4H' },
   { value: 'D',   label: 'D' },
   { value: 'W',   label: 'W' }
];

const DEFAULT_TICKER = 'SPX';
const DEFAULT_RESOLUTION = '5';

@Component({
   selector: 'ets-price-chart-panel',
   templateUrl: './price-chart-panel.component.html',
   styleUrls: ['./price-chart-panel.component.scss'],
   host: { '[class.ets-hidden]': '!isActive' },
   changeDetection: ChangeDetectionStrategy.OnPush
})
export class PriceChartPanelComponent extends PanelBaseComponent {

   constructor(
      protected readonly _changeDetector: ChangeDetectorRef,
      protected readonly _userSettingsService: UserSettingsService,
      protected readonly _messageBus: MessageBusService,
      private readonly _tiService: TradingInstrumentsService
   ) {
      super(_changeDetector, _userSettingsService, _messageBus);
      this.componentId = shortid.generate();
   }

   @ViewChild(PriceChartComponent) chartComponent: PriceChartComponent;

   readonly componentId: string;
   readonly resolutions = RESOLUTIONS;
   readonly chartTypes = CHART_TYPES;

   ticker: string = DEFAULT_TICKER;
   resolution: string = DEFAULT_RESOLUTION;
   chartType: string = DEFAULT_CHART_TYPE;
   selectedInstrument: TradingInstrument | null = null;
   maEnabled = false;
   maPeriod: number = DEFAULT_MA_PERIOD;
   maColor: string = DEFAULT_MA_COLOR;
   pivotsEnabled = false;
   pivotUpColor: string = DEFAULT_PIVOT_UP_COLOR;
   pivotDownColor: string = DEFAULT_PIVOT_DOWN_COLOR;

   private _perTicker: { [ticker: string]: PriceChartTickerState } = {};
   private _pendingOverlays: OverlaySnapshot[] | null = null;

   etsOnInit(): void {
      this._loadAndApplySettings();
   }

   etsAfterViewInit(): void {
      this._resolveInstrument();
      this._changeDetector.detectChanges();
   }

   etsOnDestroy(): void { }

   onInstrumentSelected(instrument: TradingInstrument): void {
      if (!instrument || !instrument.ticker || instrument.ticker === this.ticker) {
         return;
      }

      const oldTicker = this.ticker;
      const oldOverlays = (this._perTicker[oldTicker] && this._perTicker[oldTicker].overlays) || [];
      this._perTicker[oldTicker] = {
         resolution: this.resolution,
         chartType: this.chartType,
         maEnabled: this.maEnabled,
         maPeriod: this.maPeriod,
         maColor: this.maColor,
         pivotsEnabled: this.pivotsEnabled,
         pivotUpColor: this.pivotUpColor,
         pivotDownColor: this.pivotDownColor,
         overlays: oldOverlays
      };

      this.ticker = instrument.ticker;
      this.selectedInstrument = instrument;

      const newState = this._perTicker[this.ticker];
      if (newState) {
         this.resolution = newState.resolution || DEFAULT_RESOLUTION;
         this.chartType = (typeof newState.chartType === 'string' && newState.chartType.length > 0)
            ? newState.chartType
            : DEFAULT_CHART_TYPE;
         this.maEnabled = !!newState.maEnabled;
         this.maPeriod = (Number.isFinite(newState.maPeriod) && newState.maPeriod > 0)
            ? newState.maPeriod
            : DEFAULT_MA_PERIOD;
         this.maColor = newState.maColor || DEFAULT_MA_COLOR;
         this.pivotsEnabled = !!newState.pivotsEnabled;
         this.pivotUpColor = newState.pivotUpColor || DEFAULT_PIVOT_UP_COLOR;
         this.pivotDownColor = newState.pivotDownColor || DEFAULT_PIVOT_DOWN_COLOR;
         this._pendingOverlays = newState.overlays || [];
      } else {
         this._pendingOverlays = [];
      }

      this._persistSettings();
      this._changeDetector.detectChanges();
   }

   private _resolveInstrument(): void {
      if (!this.ticker) {
         return;
      }
      const ti = this._tiService.getInstrumentByTicker(this.ticker);
      if (ti) {
         this.selectedInstrument = ti;
      }
   }

   onResolutionChanged(): void {
      this._persistSettings();
      this._changeDetector.detectChanges();
   }

   onChartTypeChanged(): void {
      this._persistSettings();
      this._changeDetector.detectChanges();
   }

   onDrawHorizontalLine(): void {
      if (this.chartComponent) {
         this.chartComponent.createOverlay('horizontalStraightLine');
      }
   }

   onDrawVerticalLine(): void {
      if (this.chartComponent) {
         this.chartComponent.createOverlay('verticalStraightLine');
      }
   }

   onDrawTrendLine(): void {
      if (this.chartComponent) {
         this.chartComponent.createOverlay('segment');
      }
   }

   onClearDrawings(): void {
      if (this.chartComponent) {
         this.chartComponent.removeAllOverlays();
      }
   }

   onOverlaysChanged(snapshots: OverlaySnapshot[]): void {
      const ts: PriceChartTickerState = this._perTicker[this.ticker] || {
         resolution: this.resolution,
         chartType: this.chartType,
         maEnabled: this.maEnabled,
         maPeriod: this.maPeriod,
         maColor: this.maColor,
         pivotsEnabled: this.pivotsEnabled,
         pivotUpColor: this.pivotUpColor,
         pivotDownColor: this.pivotDownColor,
         overlays: []
      };
      ts.overlays = snapshots;
      this._perTicker[this.ticker] = ts;
      this._persistSettings();
      this._changeDetector.detectChanges();
   }

   onBarsLoaded(): void {
      if (!this.chartComponent || this._pendingOverlays === null) {
         return;
      }
      const toApply = this._pendingOverlays;
      this._pendingOverlays = null;
      this.chartComponent.applyOverlays(toApply);
   }

   onJumpToLatest(): void {
      if (this.chartComponent) {
         this.chartComponent.scrollToRealTime();
      }
   }

   onToggleMA(): void {
      this._persistSettings();
      this._changeDetector.detectChanges();
   }

   onMaPeriodChanged(): void {
      if (!Number.isFinite(this.maPeriod) || this.maPeriod < 1) {
         this.maPeriod = DEFAULT_MA_PERIOD;
      }
      this._persistSettings();
      this._changeDetector.detectChanges();
   }

   onMaColorChanged(): void {
      if (!this.maColor) {
         this.maColor = DEFAULT_MA_COLOR;
      }
      this._persistSettings();
      this._changeDetector.detectChanges();
   }

   onTogglePivots(): void {
      this._persistSettings();
      this._changeDetector.detectChanges();
   }

   onPivotUpColorChanged(): void {
      if (!this.pivotUpColor) {
         this.pivotUpColor = DEFAULT_PIVOT_UP_COLOR;
      }
      this._persistSettings();
      this._changeDetector.detectChanges();
   }

   onPivotDownColorChanged(): void {
      if (!this.pivotDownColor) {
         this.pivotDownColor = DEFAULT_PIVOT_DOWN_COLOR;
      }
      this._persistSettings();
      this._changeDetector.detectChanges();
   }

   private _persistSettings(): void {
      setTimeout(() => {
         const state = this._buildSettings();
         this._userSettingsService.setValue(STORAGE_KEY, state);
      }, 1);
   }

   private _loadAndApplySettings(): void {
      const raw: any = this._userSettingsService.getValue(STORAGE_KEY);
      if (!raw) {
         return;
      }
      this._applySettings(raw);
   }

   private _buildSettings(): PriceChartPanelState {
      const previousOverlays = (this._perTicker[this.ticker] && this._perTicker[this.ticker].overlays) || [];
      const current: PriceChartTickerState = {
         resolution: this.resolution,
         chartType: this.chartType,
         maEnabled: this.maEnabled,
         maPeriod: this.maPeriod,
         maColor: this.maColor,
         pivotsEnabled: this.pivotsEnabled,
         pivotUpColor: this.pivotUpColor,
         pivotDownColor: this.pivotDownColor,
         overlays: previousOverlays
      };
      this._perTicker[this.ticker] = current;
      return {
         schemaVersion: SCHEMA_VERSION,
         lastTicker: this.ticker,
         perTicker: this._perTicker
      };
   }

   private _applySettings(raw: any): void {
      if (!raw) {
         return;
      }
      if (raw.schemaVersion === SCHEMA_VERSION && raw.perTicker) {
         this._applyV2(raw as PriceChartPanelState);
         return;
      }
      const legacy = raw as LegacyPriceChartPanelState;
      const ticker = legacy.ticker || DEFAULT_TICKER;
      const migrated: PriceChartPanelState = {
         schemaVersion: SCHEMA_VERSION,
         lastTicker: ticker,
         perTicker: {
            [ticker]: {
               resolution: legacy.resolution || DEFAULT_RESOLUTION,
               chartType: (typeof legacy.chartType === 'string' && legacy.chartType.length > 0)
                  ? legacy.chartType
                  : DEFAULT_CHART_TYPE,
               maEnabled: !!legacy.maEnabled,
               maPeriod: (Number.isFinite(legacy.maPeriod) && legacy.maPeriod! > 0)
                  ? legacy.maPeriod!
                  : DEFAULT_MA_PERIOD,
               maColor: legacy.maColor || DEFAULT_MA_COLOR,
               pivotsEnabled: !!legacy.pivotsEnabled,
               pivotUpColor: legacy.pivotUpColor || DEFAULT_PIVOT_UP_COLOR,
               pivotDownColor: legacy.pivotDownColor || DEFAULT_PIVOT_DOWN_COLOR,
               overlays: []
            }
         }
      };
      this._applyV2(migrated);
      this._persistSettings();
   }

   private _applyV2(state: PriceChartPanelState): void {
      this._perTicker = state.perTicker || {};
      this.ticker = state.lastTicker || DEFAULT_TICKER;
      const tickerState = this._perTicker[this.ticker];
      if (tickerState) {
         this.resolution = tickerState.resolution || DEFAULT_RESOLUTION;
         this.chartType = (typeof tickerState.chartType === 'string' && tickerState.chartType.length > 0)
            ? tickerState.chartType
            : DEFAULT_CHART_TYPE;
         this.maEnabled = !!tickerState.maEnabled;
         this.maPeriod = (Number.isFinite(tickerState.maPeriod) && tickerState.maPeriod > 0)
            ? tickerState.maPeriod
            : DEFAULT_MA_PERIOD;
         this.maColor = tickerState.maColor || DEFAULT_MA_COLOR;
         this.pivotsEnabled = !!tickerState.pivotsEnabled;
         this.pivotUpColor = tickerState.pivotUpColor || DEFAULT_PIVOT_UP_COLOR;
         this.pivotDownColor = tickerState.pivotDownColor || DEFAULT_PIVOT_DOWN_COLOR;
         this._pendingOverlays = tickerState.overlays || [];
      } else {
         this._pendingOverlays = null;
      }
   }

   protected getState(): any {
      return null;
   }

   protected setState(_raw: any): void {
   }
}
